import UAParser from '@amplitude/ua-parser-js';
import stringify from 'json-stringify-safe';
import axios from 'axios';
import { isClassLogEnable, shouldLogToProvider, filterErrorFields, filterUnallowedExtraInfosFields } from '../utils.es.js';

class LogglyService {
    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(config, properties, $window, httpClient) {
        this.config = config;
        this.$window = $window;
        this.httpClient = httpClient;
        this.httpConfig = {
            headers: {
                'Content-Type': 'text/plain',
            },
        };
        this.savedLogs = {};
        this.savedLogsOccurrences = {};
        // Set Loggly settings from CONFIG
        this.httpUrl = `https://logs-01.loggly.com/inputs/${this.config.apiKey}/tag/${this.config.tags}/`;
        this.MAX_LOG_OCCURRENCES = this.config.maxBundleSize;
        this.httpClient = this.httpClient || axios.create();
        // Compute default static properties
        this.defaultProperties = LogglyService.computeDefaultProperties(properties);
    }
    static computeDefaultProperties(properties) {
        if (!properties.userInfos) {
            try {
                properties.userInfos = new UAParser().getResult();
            }
            catch (e) {
                // Not able to parse user-agent - Nothing major.
            }
        }
        return Object.assign({ consecutiveOccurrences: 0 }, properties);
    }
    /**
     *  To be passed to the LogService Callback so it's called after calling the console logger
     * @param event - structured event provided by the LogService
     * @param extraParameters - Do not pass objects to deep as this params will not be sent to
     * loggly stringified (max field index can be hit) and skip type validation
     */
    send(event, extraParameters = {}) {
        // Ensure we are able to send
        if (!this.config.enabled || !this.config.apiKey || (this.$window && !this.$window.navigator.onLine)) {
            return;
        }
        // Ensure we are allowed to send
        const forceSendLogForClass = isClassLogEnable(event.className, event.level, this.config.forcedModule, false);
        if (!shouldLogToProvider(event.level, this.config) && !forceSendLogForClass) {
            return;
        }
        const logglyEvent = this.prepareLogglyEvent(Object.assign(Object.assign({}, event), extraParameters), this.defaultProperties);
        const previousLog = this.savedLogs[logglyEvent.level];
        let previousLogNbCalls = this.savedLogsOccurrences[logglyEvent.level] || 0;
        if (stringify(logglyEvent) === stringify(previousLog)) {
            // This log is identical to the previous one of the same level.
            previousLogNbCalls += 1;
            this.savedLogsOccurrences[logglyEvent.level] += 1;
            if (previousLogNbCalls < this.MAX_LOG_OCCURRENCES) {
                // We haven't reached the bundle size threshold : don't send the log.
                return;
            }
            else {
                // We hit the max log bundle size : reset the counter and send the log.
                this.savedLogsOccurrences[logglyEvent.level] = 0;
                return this.post(previousLog, previousLogNbCalls);
            }
        }
        else {
            this.savedLogs[logglyEvent.level] = logglyEvent;
            this.savedLogsOccurrences[logglyEvent.level] = 0;
            if (previousLogNbCalls > 0) {
                // This log ended the series of identical logs : send it along with the current log.
                return Promise.all([this.post(previousLog, previousLogNbCalls), this.post(logglyEvent)]);
            }
            else {
                // Normal non-identical log.
                return this.post(logglyEvent);
            }
        }
    }
    post(event, consecutiveOccurrences = 0) {
        const finalEvent = this.finalizeLogglyEvent(event, consecutiveOccurrences);
        return this.httpClient.post(this.httpUrl, finalEvent, this.httpConfig).catch(() => {
            // The request was blocked by client (e.g. uBlock extension).
            console.log('Could not send log to Loggly.');
        });
    }
    /**
     * Prepare a Loggly event with most of the properties we want to send to loggly
     * with the excaption of two that would not allow us to compare logs for similar occurences and batch them.
     * finalizeLogglyEvent() will take care of adding the missing properties.
     * @param event
     * @param logglyMandatoryProperties
     */
    prepareLogglyEvent(event, logglyMandatoryProperties) {
        var _a, _b;
        let updatedErrorFields = {};
        let updatedExtraInfosFields = {};
        let extraInfosAllowedToRootLog = {};
        // We need to rework the error field as it sometimes contains so many properties that it breaks Loggly indexing.
        if (event.error) {
            // Filter out error fields to return only what we allow &
            // Add a stringified raw version so we always have access on Loggly for complete context and search
            updatedErrorFields = {
                error: filterErrorFields(event.error),
                errorRaw: typeof event.error === 'string' ? event.error : stringify(event.error),
            };
        }
        // We need to rework the extraInfos field as it sometimes could contains so many properties that it breaks Loggly indexing.
        if (event.extraInfos) {
            // Filter out extraInfos fields to return only what we allow &
            // Add a stringified raw version so we always have access on Loggly for complete context and search
            updatedExtraInfosFields = {
                extraInfos: filterUnallowedExtraInfosFields(event.extraInfos),
                extraInfosRaw: stringify(event.extraInfos),
            };
            extraInfosAllowedToRootLog = {
                userId: ((_a = event.extraInfos) === null || _a === void 0 ? void 0 : _a.userId) || undefined,
                correlationId: ((_b = event.extraInfos) === null || _b === void 0 ? void 0 : _b.correlationId) || undefined,
            };
        }
        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, logglyMandatoryProperties), event), updatedErrorFields), updatedExtraInfosFields), extraInfosAllowedToRootLog), { timestamp: undefined });
    }
    /**
     * Allow to add the last information to a loggly event.
     * We do not want to add this information until the last moment to be able to compare the log occurences and be able to batch them.
     * @param event
     * @param consecutiveOccurrences
     */
    finalizeLogglyEvent(event, consecutiveOccurrences = 0) {
        return Object.assign(Object.assign({}, event), { timestamp: new Date().toISOString(), consecutiveOccurrences });
    }
}

export { LogglyService };
